import type { PlanId, BillingId } from "@/config/checkoutConfig";

export interface RoleBlock {
  intro: string;
  bullets: string[];
}

export interface PlanPricing {
  id: PlanId;
  shortName: string;
  name: string;
  tagline: string;
  monthly: number;
  annual: number;
  fivePay: number;
  featured: boolean;
  headline: string;
  bullets: string[];
  role: RoleBlock; // Brand Affiliate responsibilities — always shown
  weHandle?: RoleBlock; // ELITE only — what WinCampaign handles
}

export const PLANS: PlanPricing[] = [
  {
    id: "premium",
    shortName: "DFY PREMIUM",
    name: "DFY Business Growth Accelerator PREMIUM",
    tagline:
      "We build your pipeline. You build the relationships. Perfect for Brand Affiliates who enjoy conducting their own Discovery Calls while our team handles the marketing and lead generation.",
    monthly: 348,
    annual: 3758,
    fivePay: 762,
    featured: false,
    headline: "Your dedicated marketing team",
    bullets: [
      "Done-for-You Social Media (20 posts/mo)",
      "Done-for-You Lead Generation",
      "Qualified Leads Delivered",
      "Monthly Growth Review",
    ],
    role: {
      intro:
        "You remain the face of your business and continue building relationships with your prospects.",
      bullets: [
        "Conduct your own Discovery Calls",
        "Reply to email inquiries and messages from qualified leads",
        "Build relationships and nurture prospects",
        "Answer questions before and after Discovery Calls",
        "Enroll new subscribers",
        "Continue serving and supporting your subscribers",
      ],
    },
  },
  {
    id: "elite",
    shortName: "DFY ELITE",
    name: "DFY Business Growth Accelerator ELITE",
    tagline:
      "We build your pipeline — and help convert your qualified prospects. A complete Done-for-You business growth solution with a dedicated Growth Advisor who conducts Discovery Calls on your behalf.",
    monthly: 498,
    annual: 5378,
    fivePay: 1086,
    featured: true,
    headline: "We handle the entire growth engine",
    bullets: [
      "Done-for-You Social Media (20 posts/mo)",
      "Done-for-You Lead Generation",
      "Lead Nurturing & Follow-up",
      "Qualified Leads Delivered",
      "Monthly Growth Review",
      "Up to 10 Discovery Calls conducted for you / month",
      "Dedicated WinCampaign Growth Advisor™",
    ],
    role: {
      intro:
        "Even with a dedicated Growth Advisor, you remain the trusted business owner and primary relationship builder.",
      bullets: [
        "Reply to inquiries and messages from qualified leads",
        "Build relationships and stay connected with prospects",
        "Answer questions before and after Discovery Calls",
        "Continue conversations after the Discovery Call when appropriate",
        "Welcome and support your new subscribers",
        "Build long-term relationships with your subscriber community",
      ],
    },
    weHandle: {
      intro:
        "WinCampaign becomes an extension of your business by helping move qualified prospects toward becoming subscribers.",
      bullets: [
        "Research and generate qualified prospects",
        "Conduct outreach campaigns",
        "Deliver qualified leads",
        "Schedule and conduct up to 10 Discovery Calls each month",
        "Professionally present the WinCampaign opportunity during Discovery Calls",
        "Recommend the most appropriate WinCampaign plan",
        "Help convert qualified prospects into new subscribers",
      ],
    },
  },
];

export const BILLING_LABEL: Record<BillingId, string> = {
  monthly: "Monthly",
  annual: "Annual",
  fivePay: "5 Easy Payments",
};

export function priceFor(plan: PlanPricing, billing: BillingId): number {
  if (billing === "monthly") return plan.monthly;
  if (billing === "annual") return plan.annual;
  return plan.fivePay;
}

export function priceSuffix(billing: BillingId): string {
  if (billing === "monthly") return "/month";
  if (billing === "annual") return "/year";
  return " × 5 payments";
}

export function currency(n: number): string {
  return `$${n.toLocaleString("en-US")}`;
}
